import { useEffect, useState } from "react";
import { Save } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { PageHeader, Field, Spinner } from "@/components/common";
import { api, formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const Section = ({ title, desc, children }) => (
  <div className="panel p-5 sm:p-6 grid lg:grid-cols-[260px_1fr] gap-6 fade-up">
    <div><h3 className="font-semibold text-slate-900">{title}</h3><p className="text-xs text-slate-500 mt-1">{desc}</p></div>
    <div className="grid sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const Toggle = ({ label, checked, onChange, testid }) => (
  <label className="flex items-center justify-between gap-3 border border-slate-200 rounded px-3 py-2.5 sm:col-span-2">
    <span className="text-sm text-slate-700">{label}</span><Switch checked={checked} onCheckedChange={onChange} data-testid={testid} />
  </label>
);

const TYPES = ["pdf", "jpg", "jpeg", "png", "webp"];
const ROLES = [["super_admin", "Super Admin"], ["staff", "Staff"], ["auditor", "Auditor"]];

export default function SettingsPage() {
  const { setSettings } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/settings").then(({ data }) => setS(data)); }, []);
  if (!s) return <Spinner />;
  const set = (k, v) => setS({ ...s, [k]: v });
  const toggleIn = (k, v) => set(k, s[k].includes(v) ? s[k].filter((x) => x !== v) : [...s[k], v]);
  const txt = (k, label, type = "text", hint) => (
    <Field label={label} hint={hint}><Input type={type} value={s[k]} onChange={(e) => set(k, type === "number" ? Number(e.target.value) : e.target.value)} data-testid={`settings-${k}`} className={type === "number" ? "num" : ""} /></Field>
  );
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/settings", s); setS(data); setSettings(data); toast.success("Settings saved"); } catch (e) { toast.error(formatErr(e)); } finally { setBusy(false); }
  };

  return (
    <div className="space-y-5">
      <PageHeader eyebrow="System" title="Master settings" subtitle="Configure branding, entry policies and security. Changes are recorded in the audit log."
        actions={<Button onClick={save} disabled={busy} className="bg-emerald-800 hover:bg-emerald-900" data-testid="settings-save-button"><Save className="w-4 h-4 mr-1.5" />{busy ? "Saving…" : "Save settings"}</Button>} />
      <Section title="Company & branding" desc="Shown in the app header and on exported PDF reports.">
        {txt("app_name", "Application name")}
        {txt("company_name", "Company name")}
        {txt("company_vat_number", "Company VAT number")}
        {txt("company_address", "Company address")}
      </Section>
      <Section title="Regional" desc="Stored in UTC, displayed in Saudi Arabia local time.">
        <Field label="Timezone"><Input value="Asia/Riyadh (AST, UTC+3)" disabled className="num" data-testid="settings-timezone" /></Field>
        <Field label="Date format"><Input value="dd/mm/yyyy" disabled className="num" /></Field>
        <Field label="Currency"><Input value="SAR — Saudi Riyal" disabled data-testid="settings-currency" /></Field>
        {txt("standard_vat_rate", "VAT %", "number", "Applied automatically to purchase bill VAT and sales VAT")}
      </Section>
      <Section title="Bill entry policies" desc="Attachments, duplicates and corrections.">
        <Toggle label="Require at least one attachment per bill" checked={s.require_attachment} onChange={(v) => set("require_attachment", v)} testid="settings-require-attachment" />
        <Toggle label="Staff may correct their own entries within 24 hours (with reason); older entries — Super Admin only" checked={s.staff_can_edit_own} onChange={(v) => set("staff_can_edit_own", v)} testid="settings-staff-edit" />
        <Toggle label="Validate KSA VAT number format (15 digits, 3…3)" checked={s.enforce_vat_format} onChange={(v) => set("enforce_vat_format", v)} testid="settings-vat-format" />
        <Field label="Duplicate invoice policy">
          <Select value={s.duplicate_policy} onValueChange={(v) => set("duplicate_policy", v)}>
            <SelectTrigger data-testid="settings-duplicate-policy"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="warn">Warn and allow with confirmation</SelectItem><SelectItem value="block">Block duplicates</SelectItem></SelectContent>
          </Select>
        </Field>
        {txt("max_file_mb", "Max file size (MB)", "number")}
        <div className="sm:col-span-2">
          <div className="text-xs font-medium text-slate-600 mb-2">Allowed attachment formats</div>
          <div className="flex flex-wrap gap-4">{TYPES.map((t) => <label key={t} className="flex items-center gap-2 text-sm uppercase num"><Checkbox checked={s.allowed_file_types.includes(t)} onCheckedChange={() => toggleIn("allowed_file_types", t)} data-testid={`settings-type-${t}`} />{t}</label>)}</div>
        </div>
      </Section>
      <Section title="Security & exports" desc="Session policy, password rules and export permissions.">
        {txt("session_hours", "Session length (hours)", "number")}
        {txt("password_min_length", "Minimum password length", "number")}
        <div className="sm:col-span-2">
          <div className="text-xs font-medium text-slate-600 mb-2">Roles permitted to export reports</div>
          <div className="flex flex-wrap gap-4">{ROLES.map(([v, l]) => <label key={v} className="flex items-center gap-2 text-sm"><Checkbox checked={s.export_roles.includes(v)} onCheckedChange={() => toggleIn("export_roles", v)} data-testid={`settings-export-${v}`} />{l}</label>)}</div>
        </div>
      </Section>
      <Section title="Retention & backup" desc="Policy configuration for audit-log retention and backup schedule.">
        {txt("audit_retention_days", "Audit log retention (days)", "number")}
        <Field label="Backup frequency">
          <Select value={s.backup_frequency} onValueChange={(v) => set("backup_frequency", v)}>
            <SelectTrigger data-testid="settings-backup"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white"><SelectItem value="daily">Daily</SelectItem><SelectItem value="weekly">Weekly</SelectItem><SelectItem value="monthly">Monthly</SelectItem></SelectContent>
          </Select>
        </Field>
      </Section>
    </div>
  );
}
